import { useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Risk } from '@rtkwlf/mr-infra-risk';
import { faChevronRight } from '@rtkwlf/pro-solid-svg-icons/faChevronRight';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { EnumCell } from '@packages/components/column-store/cell/enum-cell/enum-cell';
import { LinkCell } from '@packages/components/column-store/cell/link-cell/link-cell';
import { assetCriticalityColumnType } from '@packages/components/column-store/column/asset-criticality-column-type';
import { booleanColumnType } from '@packages/components/column-store/column/boolean-column-type';
import { datetimeColumnType } from '@packages/components/column-store/column/datetime-column-type';
import { hiddenColumnType } from '@packages/components/column-store/column/hidden-column-type';
import { longStringColumnType } from '@packages/components/column-store/column/long-string-column-type';
import { mediumStringColumnType } from '@packages/components/column-store/column/medium-string-column-type';
import { riskScoreColumnType } from '@packages/components/column-store/column/risk-score-column-type';
import { riskSeverityColumnType } from '@packages/components/column-store/column/risk-severity-column-type';
import { singleSelectColumnType } from '@packages/components/column-store/column/single-select-column-type';
import { smallStringColumnType } from '@packages/components/column-store/column/small-string-column-type';
import {
  getFilteredGridSelectOperators,
  GridSelectOperator,
  Typography,
  type GridColDef,
  Button,
  LinkRouter,
  getFilteredGridStringOperators,
  GridStringOperator,
  SkeletonInferred,
} from '@packages/fenrir/components';
import { Stack } from '@packages/fenrir/components/stack';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';
import {
  RISK_MANAGEMENT_RISK_DETAILS_ROUTE,
  RISK_MANAGEMENT_RISKS_ROUTE,
} from 'ae-constants/routes.constants';

import { CVECell } from '../../../components/cve-cell/cve-cell';
import { EllipsisMenu } from '../../../components/ellipsis-menu/ellipsis-menu';
import {
  riskAssetCategoryOptions,
  riskAssetOSOptions,
  riskAssetType,
  riskResolutionTypeOptions,
  riskSourceOptions,
  riskStateOptions,
  riskStatusOptions,
} from '../../../filter-management/select-options/select-options';
import { ALL_RISKS_COLUMN_DEFS } from '../all-risks-table.constants';
import { useUserMapAndOptions } from '../use-user-map-and-options/use-user-map-and-options';

export const useRisksTableColumns = ({
  handleUpdateButtonClick,
}: {
  handleUpdateButtonClick: (rowData: Risk[]) => void;
}) => {
  const { t: tRisks } = useTranslation('riskManagement', {
    keyPrefix: 'risks',
  });

  const { data, isLoading } = useUserMapAndOptions();
  const { userMap, userOptions } = data || {};

  const [evaM1FF] = useFeatureFlag('EVA_IN_UP_M1');

  return useMemo(() => {
    const columns: GridColDef<Risk>[] = [
      {
        ...hiddenColumnType,
        field: ALL_RISKS_COLUMN_DEFS.ID,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.ID}`),
        filterOperators: getFilteredGridStringOperators([
          GridStringOperator.Equals,
        ]),
        sortable: false,
        filterable: false,
      },
      {
        ...longStringColumnType,
        field: ALL_RISKS_COLUMN_DEFS.NAME,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.NAME}`),
        sortable: false,
        filterable: false,
      },
      {
        ...mediumStringColumnType,
        field: ALL_RISKS_COLUMN_DEFS.ASSET_NAME,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.ASSET_NAME}`),
        renderCell: (params) => {
          return (
            <LinkCell
              {...params}
              to={generatePath(`/assets/devices/${params.row.assetID}`)}
            />
          );
        },
        sortable: false,
        filterable: false,
      },
      {
        ...singleSelectColumnType,
        field: ALL_RISKS_COLUMN_DEFS.ASSET_OS_NAME,
        headerName: tRisks(
          `tableHeaders.${ALL_RISKS_COLUMN_DEFS.ASSET_OS_NAME}`,
        ),
        valueOptions: riskAssetOSOptions,
        valueGetter: (_value, row) => row.device?.os?.family,
        renderCell: (params) => (
          <EnumCell
            {...params}
            translationKey="riskManagement:risks.riskAssetOSType"
          />
        ),
        sortable: false,
      },
      {
        ...singleSelectColumnType,
        field: ALL_RISKS_COLUMN_DEFS.ASSET_CATEGORY,
        headerName: tRisks(
          `tableHeaders.${ALL_RISKS_COLUMN_DEFS.ASSET_CATEGORY}`,
        ),
        valueOptions: riskAssetCategoryOptions,
        valueGetter: (_value, row) => row.device?.category,
        renderCell: (params) => (
          <EnumCell
            {...params}
            translationKey="riskManagement:risks.riskAssetCategory"
          />
        ),
        sortable: false,
      },
      {
        ...singleSelectColumnType,
        field: ALL_RISKS_COLUMN_DEFS.STATE,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.STATE}`),
        valueOptions: riskStateOptions,
        valueGetter: (_value, row) => row.management.state,
        renderCell: (params) => (
          <EnumCell
            {...params}
            translationKey="riskManagement:risks.riskState"
          />
        ),
        minWidth: 90,
        sortable: false,
      },
      {
        ...singleSelectColumnType,
        field: ALL_RISKS_COLUMN_DEFS.STATUS,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.STATUS}`),
        valueOptions: riskStatusOptions,
        filterOperators: getFilteredGridSelectOperators([
          GridSelectOperator.Is,
        ]),
        renderCell: (params) => (
          <EnumCell
            {...params}
            translationKey="riskManagement:risks.riskStatus"
          />
        ),
        minWidth: 90,
        sortable: false,
      },
      {
        ...assetCriticalityColumnType,
        field: ALL_RISKS_COLUMN_DEFS.ASSET_CRITICALITY,
        headerName: tRisks(
          `tableHeaders.${ALL_RISKS_COLUMN_DEFS.ASSET_CRITICALITY}`,
        ),
        sortable: false,
      },
      {
        ...singleSelectColumnType,
        field: ALL_RISKS_COLUMN_DEFS.SOURCE,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.SOURCE}`),
        valueOptions: riskSourceOptions,
        renderCell: (params) => (
          <EnumCell
            {...params}
            translationKey="riskManagement:risks.riskSource"
          />
        ),
        minWidth: 80,
        sortable: false,
      },
      {
        ...riskScoreColumnType,
        field: ALL_RISKS_COLUMN_DEFS.SCORE,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.SCORE}`),
      },
      {
        ...datetimeColumnType,
        field: ALL_RISKS_COLUMN_DEFS.FIRST_DETECTED_TIME,
        headerName: tRisks(
          `tableHeaders.${ALL_RISKS_COLUMN_DEFS.FIRST_DETECTED_TIME}`,
        ),
      },
      {
        ...datetimeColumnType,
        field: ALL_RISKS_COLUMN_DEFS.LAST_DETECTED_TIME,
        headerName: tRisks(
          `tableHeaders.${ALL_RISKS_COLUMN_DEFS.LAST_DETECTED_TIME}`,
        ),
      },
      {
        ...riskSeverityColumnType,
        field: ALL_RISKS_COLUMN_DEFS.SEVERITY,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.SEVERITY}`),
        sortable: false,
      },
      {
        ...smallStringColumnType,
        field: ALL_RISKS_COLUMN_DEFS.ASSIGNEE,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.ASSIGNEE}`),
        filterOperators: getFilteredGridSelectOperators([
          GridSelectOperator.isAnyOf,
        ]),
        valueOptions: userOptions,
        type: 'singleSelect',
        valueGetter: (_value, row: Risk) => {
          return userMap && row.management.assignee
            ? userMap[row.management.assignee]
            : tRisks('unassigned');
        },
        renderCell: (params) => {
          return (
            <SkeletonInferred isLoading={isLoading}>
              <Typography variant="body2">{params.value}</Typography>
            </SkeletonInferred>
          );
        },
        sortable: false,
      },
      {
        ...riskScoreColumnType,
        field: ALL_RISKS_COLUMN_DEFS.CVSSV3,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.CVSSV3}`),
        valueGetter: (_value, row: Risk) => row.cve?.primary.cvss.v3?.score,
      },
      {
        ...datetimeColumnType,
        field: ALL_RISKS_COLUMN_DEFS.RESOLUTION_TIME,
        headerName: tRisks(
          `tableHeaders.${ALL_RISKS_COLUMN_DEFS.RESOLUTION_TIME}`,
        ),
        valueGetter: (_value, row: Risk) => row.resolution?.timestamp,
      },
      {
        ...singleSelectColumnType,
        field: ALL_RISKS_COLUMN_DEFS.RESOLUTION_TYPE,
        headerName: tRisks(
          `tableHeaders.${ALL_RISKS_COLUMN_DEFS.RESOLUTION_TYPE}`,
        ),
        valueOptions: riskResolutionTypeOptions,
        valueGetter: (_value, row: Risk) => row.resolution?.type,
        renderCell: (params) => (
          <EnumCell
            {...params}
            translationKey="riskManagement:risks.riskResolutionType"
          />
        ),
        minWidth: 200,
        sortable: false,
      },
      {
        ...datetimeColumnType,
        field: ALL_RISKS_COLUMN_DEFS.DEADLINE,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.DEADLINE}`),
        valueGetter: (_value, row: Risk) => row.management.deadline,
      },
      {
        ...smallStringColumnType,
        field: ALL_RISKS_COLUMN_DEFS.CVE,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.CVE}`),
        minWidth: 150,
        renderCell: (params) => {
          return <CVECell row={params.row} />;
        },
        sortable: false,
        filterable: false,
        resizable: false,
      },
      {
        ...booleanColumnType,
        field: ALL_RISKS_COLUMN_DEFS.CISA_KEV,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.CISA_KEV}`),
      },
      {
        ...singleSelectColumnType,
        field: ALL_RISKS_COLUMN_DEFS.ASSET_TYPE,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.ASSET_TYPE}`),
        valueOptions: riskAssetType,
        renderCell: (params) => (
          <EnumCell
            {...params}
            translationKey="riskManagement:risks.riskAssetType"
          />
        ),
        sortable: false,
      },
      {
        field: ALL_RISKS_COLUMN_DEFS.ACTIONS,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.ACTIONS}`),
        type: 'actions',
        width: 180,
        minWidth: 180,
        renderCell: (params) => {
          return (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
              <LinkRouter
                to={generatePath(RISK_MANAGEMENT_RISK_DETAILS_ROUTE, {
                  riskId: params.row.id,
                })}
                state={{ [generatePath(RISK_MANAGEMENT_RISKS_ROUTE)]: true }}
              >
                <Button endIcon={<FontAwesomeIcon icon={faChevronRight} />}>
                  {tRisks('viewRisk')}
                </Button>
              </LinkRouter>

              <EllipsisMenu
                handleButtonCallback={() =>
                  handleUpdateButtonClick([params.row])
                }
              />
            </Stack>
          );
        },
        sortable: false,
        filterable: false,
        resizable: false,
      },
      {
        ...hiddenColumnType,
        field: ALL_RISKS_COLUMN_DEFS.ASSET_ID,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.ASSET_ID}`),
        filterOperators: getFilteredGridStringOperators([
          GridStringOperator.Equals,
        ]),
      },
      {
        ...hiddenColumnType,
        field: ALL_RISKS_COLUMN_DEFS.ISSUE_ID,
        headerName: tRisks(`tableHeaders.${ALL_RISKS_COLUMN_DEFS.ISSUE_ID}`),
        filterOperators: getFilteredGridStringOperators([
          GridStringOperator.Equals,
        ]),
      },
    ];

    return evaM1FF
      ? columns
      : columns.filter(
          (column) => column.field !== ALL_RISKS_COLUMN_DEFS.ASSET_TYPE,
        );
  }, [
    tRisks,
    userOptions,
    evaM1FF,
    userMap,
    isLoading,
    handleUpdateButtonClick,
  ]);
};
